/**
 * What: Draws the chrome of a Worker-sent `group` block — a bordered option row, a card, an illustrated introduction, a
 *   grid of cards or a borderless inline row — around children the caller has already rendered. The group's explicit
 *   `style` decides the box treatment; the page infers NOTHING from the title or badge. It holds no words and no rules.
 * Transport / data-flow: Pure presentation over the `group` payload. `SectionBlocks` renders the nested blocks (so pending
 *   overlays and locks apply inside a group) and hands them in as `children`; this file only wraps them.
 */
import type { BuilderBlock, BuilderIllustration } from '@testfeed/shopify-contracts'
import { useId, type ReactNode } from 'react'
import { BUILDER_BOX_GRID_COLUMNS, BuilderBox } from './BuilderBox'
import { RequiredMark } from './RequiredMark'
import { BlockHeading } from './SectionFrame'
import { COMPARISON_ART } from '../imagery/houseArt'

type Group = Extract<BuilderBlock, { kind: 'group' }>

/** Two columns above 480px, one below — the same grid the original price-suggestion cards used. */
const GROUP_GRID_COLUMNS = '@container (inline-size <= 480px) 1fr, 1fr 1fr'

/** The old standard shopper-question grid: wider tiles than the shared BuilderBox grid, as QuestionInstrumentSection drew it. */
const SUBDUED_CARD_GRID_COLUMNS = 'repeat(auto-fit, minmax(200px, 1fr))'

/** The page's closed art registry, keyed by the Worker's illustration name. The contract parses the key, so it is known. */
const ILLUSTRATIONS: Record<BuilderIllustration, string> = { comparison: COMPARISON_ART }

export function GroupBlock({ block, children }: { block: Group; children: ReactNode }) {
  const markKey = useId()
  if (block.style === 'field-action')
    return (
      <s-grid
        gridTemplateColumns="@container (inline-size <= 480px) '1fr', 'minmax(0, 1fr) auto'"
        gap="small-200"
        alignItems="start"
      >
        {children}
      </s-grid>
    )
  // An illustrated introduction: the picture beside a block of the title and the nested blocks.
  if (block.style === 'intro') {
    const art = block.illustration ? ILLUSTRATIONS[block.illustration] : undefined
    return (
      <s-stack direction="inline" gap="base" alignItems="center">
        {art ? <img src={art} alt="" width={160} style={{ maxWidth: '100%', height: 'auto' }} /> : null}
        <div style={{ flex: '1 1 240px' }}>
          <s-stack direction="block" gap="small-200">
            {/* A true heading, like the original; drawn through SectionFrame's single s-heading owner (ratchet intact). */}
            {block.title ? <BlockHeading>{block.title}</BlockHeading> : null}
            {children}
          </s-stack>
        </div>
      </s-stack>
    )
  }
  // A two-up grid (the price suggestion cards).
  if (block.style === 'grid')
    return (
      <s-grid gridTemplateColumns={GROUP_GRID_COLUMNS} gap="small-300">
        {children}
      </s-grid>
    )
  // A grid of cards at the shared BuilderBox tile width (the image examples). A subdued grid is the old standard
  // shopper-question grid — wider minmax(200px) tiles, exactly as QuestionInstrumentSection drew them.
  if (block.style === 'card-grid')
    return (
      <s-grid
        gridTemplateColumns={block.background === 'subdued' ? SUBDUED_CARD_GRID_COLUMNS : BUILDER_BOX_GRID_COLUMNS}
        gap="base"
      >
        {children}
      </s-grid>
    )
  // A subdued example card: the large glyph centred above a heading, then the nested blocks (the image-comparison examples).
  if (block.style === 'illustration-card')
    return (
      <s-box background="subdued" padding="base" borderRadius="small-200">
        <s-stack direction="block" gap="small-200" alignItems="center">
          <span aria-hidden="true" style={{ fontSize: '3rem', lineHeight: 1.6 }}>
            {block.glyph}
          </span>
          {block.title ? <BlockHeading>{block.title}</BlockHeading> : null}
          {children}
        </s-stack>
      </s-box>
    )
  // Borderless rows: `between` keeps a trailing action in its own column even when the first side wraps.
  // Its callers are the question title/Remove and the suggestion card's Use/vote controls; neither needs a new block kind.
  if (block.style === 'inline' || block.style === 'between' || block.style === 'end') {
    const contents = (
      <>
        {block.badge ? (
          <s-box>
            <s-badge tone={block.badge.tone ?? undefined}>{block.badge.text}</s-badge>
          </s-box>
        ) : null}
        {block.title ? <s-text type="strong">{block.title}</s-text> : null}
        {children}
      </>
    )
    if (block.style === 'between')
      return (
        <s-grid gridTemplateColumns="minmax(0, 1fr) auto" gap="small-200" alignItems="start">
          {contents}
        </s-grid>
      )
    return (
      <s-stack
        direction="inline"
        gap="small-200"
        alignItems="center"
        justifyContent={block.style === 'end' ? 'end' : 'start'}
      >
        {contents}
      </s-stack>
    )
  }
  // A card: a titled box with no badge. A subdued card is the old grey shopper-question box — a plain subdued box with a
  // strong title over its content, no border, exactly as QuestionInstrumentSection drew it. Any other card reads as the
  // shared BuilderBox so cards look the same everywhere.
  if (block.style === 'card')
    return block.background === 'subdued' ? (
      <s-box background="subdued" padding="base" borderRadius="base">
        <s-stack direction="block" gap="small-200">
          {block.title ? <s-text type="strong">{block.title}</s-text> : null}
          {children}
        </s-stack>
      </s-box>
    ) : (
      <BuilderBox title={block.title ?? undefined}>{children}</BuilderBox>
    )
  // A bordered row (an option row, the original-product row): the badge pill on top, then the nested blocks. Whether the row
  // is a required choice is the Worker's call, sent as the group's own `requiredReason` (never inferred from a child). The
  // tooltip has a stable React DOM id, independent of layout nesting. `inline-between` makes the inner stack horizontal.
  const required = block.requiredReason ? { key: markKey, reason: block.requiredReason } : null
  const badgePill = block.badge ? (
    <s-box>
      <s-badge tone={block.badge.tone ?? undefined}>{block.badge.text}</s-badge>
    </s-box>
  ) : null
  const badge =
    badgePill && required ? (
      <s-stack direction="inline" gap="small-100" alignItems="center">
        {badgePill}
        <RequiredMark markKey={required.key} reason={required.reason} />
      </s-stack>
    ) : (
      badgePill
    )
  const inline = block.style === 'inline-between'
  return (
    <s-box
      borderColor="base"
      borderRadius="small-200"
      borderWidth="small-100"
      padding="small-200"
      background={block.background}
    >
      <s-stack
        direction={inline ? 'inline' : 'block'}
        gap="small-200"
        {...(inline ? { justifyContent: 'space-between', alignItems: 'center' } : {})}
      >
        {badge}
        {block.title ? <s-text type="strong">{block.title}</s-text> : null}
        {children}
      </s-stack>
    </s-box>
  )
}
